/**
 * Central config for all widget copy and design tokens.
 *
 * Copy  — every user-facing string lives here.
 * Colors — mirror the CSS custom property defaults in widget.css (:host).
 *           Later, server-supplied config will be merged in at runtime and
 *           applied as inline CSS vars on the shadow root wrapper.
 * Font  — defaults to `inherit` so the widget picks up the host page's font.
 *         Override via `--spotlight-font` CSS custom property.
 */

export const WIDGET_CONFIG = {
  // ── User-facing strings ───────────────────────────────────────────────────

  copy: {
    // TriggerBar
    triggerLabel: 'Search...',

    // Search input / Palette
    searchPlaceholder: 'Search or ask anything…',
    escHint: 'Esc',

    // Loading / thinking states
    loading: 'Loading…',
    thinking: 'Thinking…',

    // Footer
    footer: 'Powered by Spotlight',

    // Results
    noResults: 'No results found',

    // Ask AI row
    askAiIcon: '✦',
    askAiPrefix: 'Ask AI: "',
    askAiDefault: 'Ask AI anything',
    askAiSubtitle: 'Get an AI-generated answer from your site content',

    // Empty state
    suggestionsLabel: 'Try asking',
    recentPagesLabel: 'Recently visited',

    featuresLabel: 'What Spotlight can do',
    actionsLabel: 'Actions',
    shareLabel: 'Share this page',
    shareCopied: 'Link copied!',
    feedbackActionLabel: 'Send feedback',

    // Feature list (icons live in EmptyState.tsx, text here)
    features: [
      {
        id: 'search',
        label: 'AI Search + Highlight',
        description: 'Ask anything → jump to the answer → highlight in page',
        status: 'live' as const,
        statusLabel: 'Live',
      },
      {
        id: 'guided',
        label: 'Guided Answers',
        description: 'Step list → full walkthrough — navigate answers one step at a time',
        status: 'soon' as const,
        statusLabel: 'v1',
      },
      {
        id: 'voice',
        label: 'Voice Walkthrough',
        description: 'Speak your question — hands-free navigation through any content',
        status: 'soon' as const,
        statusLabel: 'v2',
      },
    ],

    // Feedback view
    feedbackBack: 'Back',
    feedbackTitle: 'Send feedback',
    feedbackPlaceholder: "What's on your mind? A bug, suggestion, or just saying hi…",
    feedbackSubmit: 'Send feedback →',
    feedbackSubmitting: 'Sending…',
    feedbackSuccessIcon: '✓',
    feedbackSuccessTitle: 'Thanks for your feedback!',
    feedbackSuccessSub: 'We read every message.',

    // Answer view
    answerIcon: '✦',
    sourcesLabel: 'Sources',
  },

  // ── Design tokens ─────────────────────────────────────────────────────────
  // These mirror the CSS custom property defaults in widget.css :host.
  // Changing values here alone has no visual effect until a future
  // "apply config to CSS vars" step is wired up in App.tsx.

  colors: {
    // Theme (themed per-project in Phase 5)
    accent: '#6366f1',
    accentHover: '#4f46e5',
    bg: '#ffffff',
    surface: '#f9fafb',
    text: '#111827',
    textMuted: '#6b7280',
    border: '#e5e7eb',
    radius: '12px',
    shadow: '0 20px 60px rgba(0, 0, 0, 0.18), 0 4px 16px rgba(0, 0, 0, 0.08)',

    // Structural chrome (less likely to be themed)
    backdropOverlay: 'rgba(0, 0, 0, 0.4)',
    triggerShadow: '0 4px 24px rgba(0, 0, 0, 0.12), 0 1px 4px rgba(0, 0, 0, 0.06)',
    triggerShadowHover: '0 8px 32px rgba(0, 0, 0, 0.16), 0 2px 8px rgba(0, 0, 0, 0.08)',

    // Ask AI gradient
    askAiBg: 'linear-gradient(135deg, #f0f0ff 0%, #faf0ff 100%)',
    askAiBgHover: 'linear-gradient(135deg, #e8e8ff 0%, #f5e8ff 100%)',

    // Semantic status
    liveBadgeText: '#059669',
    liveBadgeBg: '#d1fae5',
    liveBadgeBorder: '#a7f3d0',

    // Fixed foreground on accent backgrounds
    submitText: '#ffffff',

    // Page text-fragment highlight
    highlight: '#ffe066',
  },

  // ── Font ─────────────────────────────────────────────────────────────────
  // The widget uses `var(--spotlight-font, inherit)` throughout.
  // `inherit` means it picks up whatever font-family the host page uses.
  // Set --spotlight-font on the page (or via server config) to override.
  font: {
    family: 'inherit',
  },
} as const
